import { useState } from 'react';
import {
  Avatar, Box, ButtonBase, Divider, ListItemIcon, Menu, MenuItem, Stack, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import useLogout from '../hooks/useLogout';
import initials from '../utils/initials';

/**
 * The bar above the pages on wide screens: in the right corner the current account, then the signed in user, whose
 * menu shows their email and Logout
 */
export default function AppHeader({ session }) {
  const theme = useTheme();
  const [anchor, setAnchor] = useState(null);
  const [loggingOut, logout] = useLogout();
  const { user, account } = session ?? {};

  return (
    <Box component="header" sx={{
      display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'flex-end', gap: 2.5, height: 72,
      px: 5, flexShrink: 0, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider',
      position: 'sticky', top: 0, zIndex: theme.zIndex.appBar,
    }}>
      {account && (
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar sx={{ width: 40, height: 40, fontSize: 15, fontWeight: 600, bgcolor: 'primary.dark' }}>
            {initials(account.name)}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>{account.name}</Typography>
            <Typography variant="caption" color="text.secondary">Current account</Typography>
          </Box>
        </Stack>
      )}
      {account && user && <Divider orientation="vertical" flexItem sx={{ my: 2 }} />}
      {user && (
        <>
          <ButtonBase onClick={(event) => setAnchor(event.currentTarget)} aria-haspopup="menu"
                      aria-expanded={Boolean(anchor)} aria-label={`${user.name}, account menu`}
                      sx={{ gap: 1.25, px: 1, py: 0.75, borderRadius: 2,
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) } }}>
            <Avatar sx={{ width: 40, height: 40, fontSize: 15, fontWeight: 600, color: 'primary.main',
              bgcolor: 'sidebar.active' }}>
              {initials(user.name)}
            </Avatar>
            <Box sx={{ textAlign: 'left' }}>
              <Typography sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>{user.name}</Typography>
              <Typography variant="caption" color="text.secondary">{user.role}</Typography>
            </Box>
            <ExpandMoreIcon sx={{ color: 'text.secondary', ml: 0.5, transition: 'transform 0.2s',
              transform: anchor ? 'rotate(180deg)' : 'none' }} />
          </ButtonBase>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { mt: 1, minWidth: 240, borderRadius: 2 } } }}>
            <Box sx={{ px: 2, py: 1.25 }}>
              <Typography sx={{ fontWeight: 600 }}>{user.name}</Typography>
              {/* name falls back to the email when the user has no name; don't show it twice */}
              {(user.email || user.username) !== user.name && (
                <Typography variant="body2" color="text.secondary">{user.email || user.username}</Typography>
              )}
            </Box>
            <Divider />
            <MenuItem onClick={logout} disabled={loggingOut} sx={{ py: 1.25 }}>
              <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
              {loggingOut ? 'Logging out…' : 'Logout'}
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  );
}
